import {
  BLACK,
  WHITE,
  codeColor,
  codeType,
  EMPTY,
  ALL_SQUARES,
  fileOf,
  rankOf,
  type Color,
  type Game,
  type Move,
  type PieceType,
  type Square,
} from '@chess/core'

export interface PlacedPiece {
  /** Stable across moves, so React animates the piece instead of replacing it. */
  readonly id: string
  readonly square: Square
  readonly color: Color
  readonly type: PieceType
}

/**
 * Where every piece stands after `ply` moves, with an identity that follows a
 * piece as it travels.
 *
 * Identity is what makes the board animate: keying a piece on its square would
 * unmount and remount it on every move, so it would blink from one square to
 * the next instead of sliding. Identities are derived by replaying the move
 * history from the start, which means the same ply always yields the same ids —
 * important when scrubbing back and forth through a finished game.
 */
export const layoutAtPly = (game: Game, ply: number): readonly PlacedPiece[] => {
  const occupants = new Map<Square, PlacedPiece>()

  for (const square of ALL_SQUARES) {
    const code = game.initial.board[square] as number
    if (code === EMPTY) continue
    occupants.set(square, {
      id: `p${square}`,
      square,
      color: codeColor(code),
      type: codeType(code),
    })
  }

  const limit = Math.min(ply, game.history.length)
  for (let index = 0; index < limit; index += 1) {
    apply(occupants, (game.history[index] as { move: Move }).move)
  }

  return [...occupants.values()]
}

const relocate = (occupants: Map<Square, PlacedPiece>, from: Square, to: Square): PlacedPiece | null => {
  const piece = occupants.get(from)
  if (piece === undefined) return null
  occupants.delete(from)
  const moved = { ...piece, square: to }
  occupants.set(to, moved)
  return moved
}

const apply = (occupants: Map<Square, PlacedPiece>, move: Move): void => {
  switch (move.kind) {
    case 'normal':
    case 'doublePush':
      relocate(occupants, move.from, move.to)
      return

    case 'enPassant': {
      const mover = occupants.get(move.from)
      // The captured pawn is beside the destination, not on it.
      const capturedSquare = ((mover?.color === WHITE ? move.to - 16 : move.to + 16) as Square)
      occupants.delete(capturedSquare)
      relocate(occupants, move.from, move.to)
      return
    }

    case 'castle':
      relocate(occupants, move.from, move.to)
      relocate(occupants, move.rookFrom, move.rookTo)
      return

    case 'promotion': {
      // Keep the identity so the pawn slides forward and then becomes a queen,
      // rather than one piece vanishing as another appears.
      const promoted = relocate(occupants, move.from, move.to)
      if (promoted !== null) occupants.set(move.to, { ...promoted, type: move.promoteTo })
      return
    }
  }
}

export interface ViewCoords {
  readonly x: number
  readonly y: number
}

/**
 * Square to board position in units of one square, honouring the flip. Multiply
 * by 100 for a percentage transform on a piece that is 12.5% wide.
 */
export const coordsOf = (square: Square, orientation: Color): ViewCoords => {
  const file = fileOf(square)
  const rank = rankOf(square)
  return orientation === WHITE ? { x: file, y: 7 - rank } : { x: 7 - file, y: rank }
}

/** The squares in the order they should be rendered, top-left first. */
export const squaresInViewOrder = (orientation: Color): readonly Square[] => {
  const ordered = [...ALL_SQUARES]
  ordered.sort((a, b) => {
    const left = coordsOf(a, orientation)
    const right = coordsOf(b, orientation)
    return left.y === right.y ? left.x - right.x : left.y - right.y
  })
  return ordered
}

export const orientationLabel = (orientation: Color): string =>
  orientation === BLACK ? 'black' : 'white'
